// AGC: FarmProHub Home Dashboard
// AGC: Shows summary widgets for Staking, Revenue, and Proposals

import React, { useState, useEffect } from 'react'
import { useAccount } from 'wagmi'
import { motion } from 'framer-motion'
import { Coins, TrendingUp, Vote, ArrowRight, Users, Wallet, GitCommit, Calendar, ChevronRight } from 'lucide-react'
import { StakingSummary } from './dashboards/StakingSummary'
import { RevenueSummary } from './dashboards/RevenueSummary'
import { ProposalSummary } from './dashboards/ProposalSummary'
import { useStaking } from '../hooks/useStaking'
import { useRevenues } from '../hooks/useRevenues'
import { formatEther } from 'viem'
import { parseChangelog, sortEntriesByDate, ChangelogEntry } from '../utils/parseChangelog'

interface HomeProps {
  onNavigateToPage: (page: string, options?: { section?: string; expandedEntry?: number | null }) => void
}

export const Home: React.FC<HomeProps> = ({ onNavigateToPage }) => {
  const { address, isConnected } = useAccount()
  const { stats } = useStaking()
  const { stats: revenueStats } = useRevenues()
  const [latestEntry, setLatestEntry] = useState<ChangelogEntry | null>(null)
  const [loadingChangelog, setLoadingChangelog] = useState(true)

  // AGC: Format FarmProScore (stakingScore from contract - returns 0-1e18, representing 0-1.0)
  // AGC: Convert to percentage (0-100) for display
  const farmProScore = stats.stakingScore ? Number(formatEther(stats.stakingScore)) * 100 : 0

  // AGC: Format number helper (for displaying staked amounts)
  const formatNumber = (value: bigint | number, decimals: number = 2) => {
    if (typeof value === 'bigint') {
      return Number(formatEther(value)).toFixed(decimals)
    }
    return Number(value).toFixed(decimals)
  }

  // AGC: Helper function to correct dates for specific versions
  const getCorrectedDate = (entry: ChangelogEntry | null): string => {
    if (!entry) return ''
    // AGC: Correct date for version 6.10.7 (should be 2025-12-05, not 2025-12-02)
    if (entry.version === '6.10.7' && entry.source === 'origami') {
      return '2025-12-05'
    }
    return entry.date
  }

  // AGC: Load latest changelog entry on mount
  useEffect(() => {
    const loadLatestChangelog = async () => {
      try {
        setLoadingChangelog(true)
        
        // AGC: Fetch both changelogs from public folder
        const [hubResponse, origamiResponse] = await Promise.all([
          fetch('/CHANGELOG_hub.md').catch(() => null),
          fetch('/changelog_origami.md').catch(() => null),
        ])
        
        const entries: ChangelogEntry[] = []
        
        if (hubResponse?.ok) {
          const hubText = await hubResponse.text()
          const hubEntries = parseChangelog(hubText, 'hub')
          entries.push(...hubEntries)
        }
        
        if (origamiResponse?.ok) {
          const origamiText = await origamiResponse.text()
          const origamiEntries = parseChangelog(origamiText, 'origami')
          entries.push(...origamiEntries)
        }
        
        // AGC: Sort by date (newest first) and get latest entry
        const sortedEntries = sortEntriesByDate(entries)
        if (sortedEntries.length > 0) {
          setLatestEntry(sortedEntries[0])
        }
      } catch (error) {
        console.error('AGC: Error loading latest changelog:', error)
      } finally {
        setLoadingChangelog(false)
      }
    }
    
    loadLatestChangelog()
  }, [])

  // AGC: Handle click on latest changelog entry - navigate to docs with changelog expanded
  const handleChangelogClick = () => {
    // AGC: Navigate to docs page with changelog section active and first entry (index 0) expanded
    // AGC: MainMenu will pass these options to DocsPage
    onNavigateToPage('docs', { section: 'changelog', expandedEntry: 0 })
  }

  return (
    <main className="pt-[72px] pb-[88px] px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* AGC: Welcome Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center py-6 relative"
        >
          {/* AGC: FPscore Panel - Top Right */}
          {isConnected && (
            <div className="absolute top-0 right-0">
              <div className="bg-slate-800/80 border border-slate-700/50 rounded-lg px-3 py-1.5">
                <span className="text-xs font-semibold text-white">FPscore: </span>
                <span className="text-xs font-semibold text-[#0072CE]">{farmProScore.toFixed(2)}%</span>
              </div>
            </div>
          )}
          <h1 className={`text-2xl sm:text-3xl font-bold text-white mb-2 ${isConnected ? 'pt-10' : ''}`}>
            Welcome to <span className="text-[#0072CE]">FarmProHub</span>
          </h1>
          <p className="text-slate-400 text-sm sm:text-base">
            Stake, vote, and earn from all FarmPro projects
          </p>
        </motion.div>

        {/* AGC: Latest Changelog Entry Panel */}
        {!loadingChangelog && latestEntry && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden cursor-pointer hover:bg-slate-800/70 transition-colors"
            onClick={handleChangelogClick}
          >
            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <GitCommit className="w-4 h-4 text-[#0072CE]" />
                  <span className="text-xs font-semibold text-slate-400">Latest Update</span>
                </div>
                <ChevronRight className="w-4 h-4 text-slate-400" />
              </div>
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-2 h-2 rounded-full ${
                  latestEntry.source === 'hub' ? 'bg-[#0072CE]' : 'bg-purple-500'
                }`} />
                <span className="text-xs font-semibold text-slate-400">
                  {latestEntry.source === 'hub' ? 'Hub' : 'Origami'}
                </span>
                <span className="text-sm font-semibold text-white">
                  v{latestEntry.version}
                </span>
                <span className="text-sm text-slate-300 truncate flex-1">
                  {latestEntry.title}
                </span>
              </div>
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <Calendar className="w-3 h-3" />
                <span>{getCorrectedDate(latestEntry)}</span>
              </div>
            </div>
          </motion.div>
        )}

        {/* AGC: Quick Stats Row */}
        {isConnected && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="grid grid-cols-2 sm:grid-cols-4 gap-3"
          >
            <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
              <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
                <Wallet className="w-3 h-3" />
                <span>Your Stake</span>
              </div>
              <p className="text-white font-bold text-lg">{formatNumber(stats.yourStake)} OGB</p>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
              <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
                <TrendingUp className="w-3 h-3" />
                <span>Your Share</span>
              </div>
              <p className="text-[#0072CE] font-bold text-lg">{stats.yourShare.toFixed(2)}%</p>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
              <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
                <Coins className="w-3 h-3" />
                <span>Claimable</span>
              </div>
              <p className="text-[#0072CE] font-bold text-lg">
                {new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(revenueStats.claimable)}
              </p>
            </div>
            <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
              <div className="flex items-center gap-2 text-slate-400 text-xs mb-1">
                <Users className="w-3 h-3" />
                <span>Total Staked</span>
              </div>
              <p className="text-white font-bold text-lg">{formatNumber(stats.totalStaked)} OGB</p>
            </div>
          </motion.div>
        )}

        {/* AGC: Not Connected Message */}
        {!isConnected && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-slate-800/50 rounded-xl p-6 border border-slate-700/50 text-center"
          >
            <Wallet className="w-12 h-12 text-slate-500 mx-auto mb-4" />
            <h3 className="text-white font-semibold mb-2">Connect Your Wallet</h3>
            <p className="text-slate-400 text-sm">
              Connect your wallet to view your staking position and participate in governance.
            </p>
          </motion.div>
        )}

        {/* AGC: Dashboard Widgets */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* AGC: Staking Summary Widget */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <StakingSummary onViewDetails={() => onNavigateToPage('staking')} />
          </motion.div>

          {/* AGC: Revenue Summary Widget */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <RevenueSummary onViewDetails={() => onNavigateToPage('revenues')} />
          </motion.div>
        </div>

        {/* AGC: Proposals Summary Widget (full width) */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <ProposalSummary onViewDetails={() => onNavigateToPage('proposals')} />
        </motion.div>

        {/* AGC: Projects Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="bg-slate-800/50 rounded-xl p-5 border border-slate-700/50"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <span className="text-xl">🎮</span>
              Revenue-Sharing Projects
            </h3>
          </div>
          
          {/* AGC: Project Card - Origami Battles */}
          <div className="bg-slate-700/50 rounded-lg p-4 border border-purple-500/20 hover:border-purple-500/40 transition-all">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">🎴</span>
                </div>
                <div>
                  <h4 className="text-white font-semibold">Origami Battles</h4>
                  <p className="text-slate-400 text-xs">Strategic battle game on Base</p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-[#0072CE] font-bold">
                  {new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }).format(revenueStats.totalRevenue)}
                </p>
                <p className="text-slate-400 text-xs">Total Revenue</p>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-slate-600/50 flex items-center justify-between">
              <span className="text-xs text-slate-400">Status: <span className="text-[#0072CE]">Live</span></span>
              <a 
                href="https://origamibattle.app" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-xs text-purple-400 hover:text-purple-300 flex items-center gap-1"
              >
                Visit App <ArrowRight className="w-3 h-3" />
              </a>
            </div>
          </div>

          {/* AGC: Coming Soon Placeholder */}
          <div className="mt-3 bg-slate-700/30 rounded-lg p-4 border border-dashed border-slate-600">
            <p className="text-center text-slate-500 text-sm">
              More projects coming soon...
            </p>
          </div>
        </motion.div>
      </div>
    </main>
  )
}

